/**
 * Markdown files with YAML frontmatter, read and written without touching untouched parts.
 *
 * An entry's data is the frontmatter plus the Markdown body under the key `body` (the same convention as
 * Sveltia/Decap). The `---` fences, line endings and body are kept exactly as found.
 */
import { parseYaml, stringifyYaml, updateYaml, type YamlValue } from './yaml-edit.js';

export interface MarkdownFile {
  /** frontmatter values */
  data: Record<string, YamlValue>;
  body: string;
}

const FENCE = /^---[ \t]*(\r?\n)([\s\S]*?)(?:\r?\n)?---[ \t]*(?:\r?\n|$)/;

interface Split {
  frontmatter: string;
  body: string;
  newline: string;
  hasFrontmatter: boolean;
}

function split(text: string): Split {
  const bom = text.startsWith('\uFEFF') ? '\uFEFF' : '';
  const t = text.slice(bom.length);
  const m = t.match(FENCE);
  if (!m) return { frontmatter: '', body: text, newline: '\n', hasFrontmatter: false };
  return { frontmatter: m[2]!, body: t.slice(m[0].length), newline: m[1]!, hasFrontmatter: true };
}

export function parseMarkdown(text: string): MarkdownFile {
  const s = split(text);
  const data = s.frontmatter.trim() ? parseYaml(s.frontmatter + '\n') : {};
  if (data === null || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('Frontmatter must be a YAML mapping');
  }
  return { data: data as Record<string, YamlValue>, body: s.body };
}

/** Write `next` into the original file text, changing only what differs. */
export function updateMarkdown(original: string, next: MarkdownFile): string {
  const s = split(original);
  if (!s.hasFrontmatter) return renderMarkdown(next);
  const fmSource = s.frontmatter.replace(/\r\n/g, '\n') + '\n';
  const fm = updateYaml(fmSource, next.data);
  const head = original.slice(0, original.length - s.body.length);
  let out: string;
  if (fm === fmSource) {
    out = head;
  } else {
    const bom = original.startsWith('\uFEFF') ? '\uFEFF' : '';
    out = `${bom}---${s.newline}${fm.replace(/\n$/, '').replace(/\n/g, s.newline)}${s.newline}---${s.newline}`;
  }
  return out + (next.body === s.body ? s.body : next.body);
}

/** A new Markdown file (frontmatter keys in the given order). */
export function renderMarkdown(file: MarkdownFile): string {
  const keys = Object.keys(file.data);
  const fm = keys.length ? stringifyYaml(file.data) : '';
  const body = file.body ?? '';
  return `---\n${fm}---\n${body}`;
}
